import { UI_CONFIG } from '@porterin/booking-flow/src/auth/domain/config/ui';
import { Customer } from '@porterin/booking-flow/src/auth/domain/entities/Customer';
import { authRepo } from '@porterin/booking-flow/src/auth/domain/repo/authRepo';
import { NetworkError } from '@porterin/booking-flow/src/auth/domain/types/errors';
import { publishNewUserSignupEvent } from '@porterin/booking-flow/src/auth/view/helpers/AuthAnalyticsHelper';
import { AgreedDocs } from '@porterin/booking-flow/src/order-review/domain/entities/AgreedDocs';
import { CustomerAddress } from '@porterin/booking-flow/src/order-review/domain/entities/CustomerAddress';
import { User } from '@porterin/booking-flow/src/order-review/view/contexts/auth/AuthContext';
import {
  getLocaleCookie,
  getTrackingCookieSafe,
  setCookieWithExpiration,
} from '@porterin/booking-flow/src/order-review/view/helpers/CookieHelper';
import { COOKIE_KEYS } from '@porterin/booking-flow/src/order-review/view/stores/OrderReviewStore';
import { AppConfig } from '@porterin/component-library/src/ts-utils/analytics/fetch-config/AppConfig';
import { getAppConfig } from '@porterin/component-library/src/ts-utils/analytics/fetch-config/FetchConfig';
import { Dispatch, SetStateAction } from 'react';
const Config: AppConfig = getAppConfig();

export const orderReviewLinkCreator = (
  sender: CustomerAddress,
  receiver: CustomerAddress,
  vehicleId: number,
  user?: User,
): string => {
  const userMobile = user?.mobile ?? '';
  const senderMobile = sender.mobile ?? userMobile;

  return `/${getLocaleCookie()}/order-review?sender_mobile=${senderMobile}&sender_name=${
    sender.name ?? 'Sender'
  }&receiver_mobile=${receiver.mobile ?? ''}&receiver_name=${
    receiver.name ?? 'Receiver'
  }&from_landmark=${sender.pickupAddress.landmark}&from_lat=${
    sender.pickupAddress.location.lat
  }&from_long=${sender.pickupAddress.location.lng}&from_house=${
    sender.house ?? ''
  }&to_landmark=${receiver.dropAddress.landmark}&to_lat=${
    receiver.dropAddress.location.lat
  }&to_long=${receiver.dropAddress.location.lng}&to_house=${
    receiver.house ?? ''
  }&vehicle_id=${vehicleId}`;
};

const onOtpSuccess = (
  data: Customer,
  user: User | null,
  set_user: Dispatch<SetStateAction<User | null>>,
  close_modal: () => void,
  on_otp_valid?: () => void,
  modal_close_timeout: number = UI_CONFIG.MODAL_CLOSE_TIMEOUT,
) => {
  setCookieWithExpiration(COOKIE_KEYS.AUTH_TOKEN, data.authToken, 7);
  setCookieWithExpiration(COOKIE_KEYS.MOBILE, data.mobile, 7);
  set_user(prev => ({
    ...prev,
    authToken: data.authToken,
    mobile: data.mobile,
    name: user?.name,
    requirementType: user?.requirementType,
    email: user?.email,
    whatsappConsent: user?.whatsappConsent,
  }));
  setTimeout(() => {
    close_modal();
    on_otp_valid && on_otp_valid();
  }, modal_close_timeout);
};

async function validateOtpIfExists(
  tracking_id: string,
  user: User | null,
  otp: string,
  close_modal: () => void,
  set_user: Dispatch<SetStateAction<User | null>>,
  set_error: (error: string) => void,
  on_otp_valid?: () => void,
  modal_close_timeout?: number,
  agreed_docs?: AgreedDocs,
) {
  try {
    const response = await authRepo.validateOtp(
      tracking_id,
      {
        mobile: user?.mobile || '',
        email: user?.email || '',
        otp: otp,
        name: user?.name || '',
        whatsappConsent: user?.whatsappConsent,
        customerFrequency: user?.requirementType,
      },
      agreed_docs,
    );
    onOtpSuccess(
      response,
      user,
      set_user,
      close_modal,
      on_otp_valid,
      modal_close_timeout,
    );
  } catch (error: NetworkError | any) {
    if (error?.type === 'NetworkError') {
      set_error(error.message);
    } else {
      set_error(typeof error == 'string' ? error : 'Something went wrong');
    }
  }
}

async function validateOtpIfNotExists(
  tracking_id: string,
  user: User | null,
  otp: string,
  close_modal: () => void,
  set_user: Dispatch<SetStateAction<User | null>>,
  set_error: (error: string) => void,
  on_otp_valid?: () => void,
  modal_close_timeout?: number,
  agreed_docs?: AgreedDocs,
) {
  const queryString = new URLSearchParams(window.location.search);
  const referralCode = queryString.get('referral_code');
  const geoRegionId = queryString.get('geo_region_id');

  try {
    const response = await authRepo.validateOtp(
      tracking_id,
      {
        mobile: user?.mobile || '',
        email: user?.email || '',
        otp: otp,
        name: user?.name || '',
        whatsappConsent: user?.whatsappConsent,
        customerFrequency: user?.requirementType,
      },
      agreed_docs,
      referralCode || undefined,
      geoRegionId || undefined,
    );
    onOtpSuccess(
      response,
      user,
      set_user,
      close_modal,
      on_otp_valid,
      modal_close_timeout,
    );
  } catch (error: NetworkError | any) {
    if (error?.type === 'NetworkError') {
      set_error(error.message);
    } else {
      set_error(typeof error == 'string' ? error : 'Something went wrong');
    }
  }
}

export const handleOtpSubmit = async (
  otp: string,
  set_user: Dispatch<SetStateAction<User | null>>,
  set_error: (error: string) => void,
  close_modal: () => void,
  user: User | null,
  on_otp_valid?: () => void,
  modal_close_timeout?: number,
  agreed_docs?: AgreedDocs,
) => {
  const trackingId = getTrackingCookieSafe();

  publishNewUserSignupEvent(user?.mobile || '');

  try {
    const doesCustomerExist = await authRepo.checkCustomerExists(
      user?.mobile || '',
    );
    if (doesCustomerExist.exists == true) {
      await validateOtpIfExists(
        trackingId,
        user,
        otp,
        close_modal,
        set_user,
        set_error,
        on_otp_valid,
        modal_close_timeout,
        agreed_docs,
      );
    } else {
      await validateOtpIfNotExists(
        trackingId,
        user,
        otp,
        close_modal,
        set_user,
        set_error,
        on_otp_valid,
        modal_close_timeout,
        agreed_docs,
      );
    }
  } catch (error: NetworkError | any) {
    if (error?.type === 'NetworkError') {
      set_error(error.message);
    } else {
      set_error(typeof error == 'string' ? error : 'Something went wrong');
    }
  }
};
